import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type ShopListItem } from '../api';
import { useReveal } from '../motion';
import { num, str } from './types';
import { ProductCard } from './ProductCard';

/**
 * Pas zbozi z jedne kategorie.
 *
 * Na sirokem displeji je to mrizka, na uzkem vodorovny posuvnik – zbozi
 * se tak vejde i na mobil bez nekonecneho rolovani dolu.
 *
 * Kdyz kategorie neexistuje (obsluha ji mezitim smazala) nebo je prazdna,
 * blok se neukaze vubec. Prazdny pas s nadpisem vypada jako chyba.
 */
export function ProductRowBlock({ config }: { config: Record<string, unknown> }) {
  const title = str(config, 'title');
  const source = str(config, 'source', 'category');
  const category = str(config, 'category');
  const linkLabel = str(config, 'linkLabel');
  const limit = num(config, 'limit', 8);

  const [items, setItems] = useState<readonly ShopListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref, className } = useReveal<HTMLElement>();

  useEffect(() => {
    let alive = true;
    // Zdroj pasu: novinky / nejprodavanejsi / vyber podle kategorie (vychozi).
    const load =
      source === 'newest'
        ? api.newest(limit)
        : source === 'bestsellers'
          ? api.bestsellers()
          : api.listProducts({ category, limit });
    load
      .then((result) => {
        if (alive) setItems(result.items);
      })
      .catch(() => {
        if (alive) setItems([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [source, category, limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section ref={ref} className={`block product-row ${className}`}>
      <div className="block-head">
        {title !== '' && <h2 className="block-title">{title}</h2>}
        {/* Odkaz „na vše" dává smysl jen u pásu podle kategorie – novinky ani
            bestsellery nemají samostatnou stránku výpisu. */}
        {linkLabel !== '' && source === 'category' && (
          <Link
            className="block-more"
            to={category === '' ? '/' : `/?kategorie=${encodeURIComponent(category)}`}
          >
            {linkLabel} <span aria-hidden="true">→</span>
          </Link>
        )}
      </div>

      <div className="rail">
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="card skeleton-card" aria-hidden="true">
                <div className="skeleton thumb" />
                <div className="product-body">
                  <div className="skeleton line short" />
                  <div className="skeleton line" />
                  <div className="skeleton line tiny" />
                </div>
              </div>
            ))
          : items.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
      </div>
    </section>
  );
}
